import classNames from 'classnames';
import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { useForm } from 'react-hook-form';

import { FormField, Input, Text } from '@revelio/ui';

import type { SnowflakeDeliveryConfig } from '../../data-builder.store';
import type { DeliveryFormHandle } from './DeliveryConfigForm.types';
import styles from './step3-delivery.module.css';

type SnowflakeConfigFormProps = {
  initialValue: SnowflakeDeliveryConfig | null;
  defaultDatabase: string | null;
  defaultSchema: string | null;
  isRevelioAdmin: boolean;
  onSave: (value: SnowflakeDeliveryConfig) => void;
};

type SnowflakeFormValues = {
  database: string;
  schema: string;
  table: string;
};

const validNamePattern = /^[A-Za-z_][A-Za-z0-9_$]*$/;

export const SnowflakeConfigForm = forwardRef<
  DeliveryFormHandle,
  SnowflakeConfigFormProps
>(function SnowflakeConfigForm(
  { initialValue, defaultDatabase, defaultSchema, isRevelioAdmin, onSave },
  ref
) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<SnowflakeFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {
      database: initialValue?.database ?? defaultDatabase ?? '',
      schema: initialValue?.schema ?? defaultSchema ?? '',
      table: initialValue?.table ?? '',
    },
  });

  useEffect(() => {
    if (isDirty) {
      return;
    }
    reset({
      database: initialValue?.database ?? defaultDatabase ?? '',
      schema: initialValue?.schema ?? defaultSchema ?? '',
      table: initialValue?.table ?? '',
    });
  }, [defaultDatabase, defaultSchema, initialValue, isDirty, reset]);

  useImperativeHandle(
    ref,
    () => ({
      submit: () =>
        new Promise((resolve) => {
          void handleSubmit(
            (value) => {
              // Non-admins omit database/schema; the backend fills them from
              // the client's settings.
              onSave(
                isRevelioAdmin
                  ? {
                      database: value.database,
                      schema: value.schema,
                      table: value.table,
                    }
                  : { table: value.table }
              );
              resolve(true);
            },
            () => {
              resolve(false);
            }
          )();
        }),
      isDirty: () => isDirty,
    }),
    [handleSubmit, isDirty, isRevelioAdmin, onSave]
  );

  return (
    <div>
      {!isRevelioAdmin && (
        <Text fontSize="sm" className={styles.fieldSpacer}>
          Database and schema are pre-configured for your account.
        </Text>
      )}
      {isRevelioAdmin && (
        <>
          <FormField
            label="Database"
            errors={
              errors.database?.message !== undefined
                ? [errors.database.message]
                : undefined
            }
            className={classNames(styles.field, styles.fieldSpacer)}
          >
            <Input
              placeholder="e.g. database"
              {...register('database', {
                required: 'Database is required',
                pattern: {
                  value: validNamePattern,
                  message:
                    'Names can only contain letters, numbers, underscores and dollar signs.',
                },
              })}
            />
          </FormField>

          <FormField
            label="Schema"
            errors={
              errors.schema?.message !== undefined
                ? [errors.schema.message]
                : undefined
            }
            className={classNames(styles.field, styles.fieldSpacer)}
          >
            <Input
              placeholder="e.g. schema"
              {...register('schema', {
                required: 'Schema is required',
                pattern: {
                  value: validNamePattern,
                  message:
                    'Names can only contain letters, numbers, underscores and dollar signs.',
                },
              })}
            />
          </FormField>
        </>
      )}

      <FormField
        label="Table"
        errors={
          errors.table?.message !== undefined
            ? [errors.table.message]
            : undefined
        }
        className={styles.field}
      >
        <Input
          placeholder="e.g. my_table"
          {...register('table', {
            required: 'Table is required',
            pattern: {
              value: validNamePattern,
              message:
                'Names can only contain letters, numbers, underscores and dollar signs.',
            },
          })}
        />
      </FormField>
    </div>
  );
});
